import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Store } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { getFavorites, toggleFavorite } from "@/lib/favorites";
import MenuButton from "@/components/mobile/MenuButton";

export default function MobileFavorites() {
  const { user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [list, setList] = useState([]);

  const refresh = useCallback(() => {
    setList(getFavorites(user?.email));
  }, [user?.email]);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener("nd_favorites_changed", onChange);
    return () => window.removeEventListener("nd_favorites_changed", onChange);
  }, [refresh]);

  const unfav = (shop) => {
    toggleFavorite(user?.email, shop);
    refresh();
  };

  return (
    <div className="px-5 pt-4">
      <div className="flex items-start justify-between">
        <h1 className="font-display font-extrabold text-3xl tracking-tight">{t("favorites.title")}</h1>
        <MenuButton />
      </div>

      <div className="h-px bg-border my-5" />

      {list.length === 0 ? (
        <div className="grid place-items-center py-24 text-center">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-secondary text-primary mb-4">
            <Heart className="w-7 h-7" />
          </span>
          <p className="font-display font-bold text-xl">{t("favorites.none")}</p>
          <p className="text-muted-foreground text-sm mt-1">{t("favorites.hint")}</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="favorites-list">
          {list.map((s) => (
            <li
              key={s.id}
              data-testid={`fav-${s.id}`}
              className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3"
            >
              <button
                onClick={() => nav(`/shops/${s.id}`)}
                className="w-20 h-20 rounded-xl overflow-hidden bg-secondary shrink-0 grid place-items-center"
              >
                {s.image ? (
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
                ) : (
                  <Store className="w-7 h-7 text-primary" />
                )}
              </button>
              <button
                onClick={() => nav(`/shops/${s.id}`)}
                className="flex-1 min-w-0 text-left"
              >
                <p className="font-extrabold uppercase truncate">{s.name}</p>
                <p className="text-primary text-sm font-semibold capitalize mt-0.5 truncate">
                  {s.category || "—"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{s.address || ""}</p>
              </button>
              <button
                data-testid={`fav-toggle-${s.id}`}
                onClick={() => unfav(s)}
                aria-label="Remove favorite"
                className="grid place-items-center w-10 h-10 rounded-full hover:bg-secondary transition-colors shrink-0"
              >
                <Heart className="w-6 h-6 fill-primary text-primary" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
